"use client";

import { c as _c } from "react/compiler-runtime";
import { textVariants } from "@/components/ui/Text";
import { headingVariants } from "@/components/ui/Heading";
import { useDialogStack } from "@/components/ui/Dialog/dialog-stack";
import { OrderedOverlayProvider } from "@/components/ui/ordered-overlay";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";
import cn from "twsx";

// Dialog (Figma: Composite / Dialog). A centred surface over a scrim:
// radius 12, border-popover, elevation-modal. Sizes sm 440 · md 562 · lg 750 ·
// xl 900, and `full` for document and media viewers (96vw × 92dvh).
// `floating` is the movable, resizable media preview and `docked` its
// non-modal form pinned to the top right while the page stays usable.
// `command` is the ⌘K surface: 640 wide, pinned 12vh from the top so it does
// not move as results change, and full-screen below md. Centring uses
// margins, not transforms. Dialogs open, close and resize without motion.

/** Use when a task needs focused, blocking input or confirmation over the current page. */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
const sizeClass = {
  sm: "top-dialog-inset-sm w-dialog-sm max-h-dialog-fit-sm max-w-viewport-inset",
  md: "top-dialog-inset-sm w-dialog-md max-h-dialog-fit-sm max-w-viewport-inset",
  lg: "top-dialog-inset-lg w-dialog-lg max-h-dialog-fit-lg max-w-viewport-inset rounded-modal",
  xl: "top-dialog-inset-xl w-dialog-xl max-h-dialog-fit-xl max-w-viewport-inset rounded-modal",
  full: "top-dialog-inset-full h-dialog-fit-full w-dialog-full rounded-modal",
  floating: "top-dialog-inset-lg h-dialog-floating w-viewport-inset max-w-4xl rounded-modal",
  docked: "inset-x-auto top-4 right-4 m-0 size-dialog-docked max-h-viewport-inset-y max-w-viewport-inset rounded-modal",
  command: "max-md:inset-0 max-md:h-dvh max-md:w-full max-md:rounded-none max-md:border-0 md:top-dialog-inset-command md:w-dialog-command md:max-h-viewport-inset-y md:max-w-viewport-inset"
} as const;
export type DialogSize = keyof typeof sizeClass;
export function DialogContent({
  size = "md",
  showClose = size !== "command",
  className,
  children,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  size?: DialogSize;
  showClose?: boolean;
}) {
  const {
    contentRef,
    depth,
    id,
    index
  } = useDialogStack();
  const composedRef = React.useCallback((node: HTMLDivElement | null) => {
    contentRef(node);
    if (typeof ref === "function") ref(node);else if (ref && typeof ref === "object") (ref as {
      current: HTMLDivElement | null;
    }).current = node;
  }, [contentRef, ref]);
  return <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay data-dialog-overlay-stack-id={id} data-dialog-stack-index={index} className="fixed inset-0 z-modal bg-scrim" />
      <DialogPrimitive.Content ref={composedRef} data-dialog-stack-depth={depth || undefined} data-dialog-stack-id={id} data-dialog-stack-index={index} data-dialog-stacked={depth > 0 || undefined} className={cn("fixed inset-x-0 z-modal mx-auto flex flex-col overflow-hidden", "[&>form]:flex [&>form]:min-h-0 [&>form]:flex-1 [&>form]:flex-col", "rounded-popover border border-border-popover bg-bg-surface text-text-primary shadow-modal outline-none", sizeClass[size], className)} {...props}>
        <OrderedOverlayProvider>
          {size === "command" ? <div className="flex flex-col max-md:h-full max-md:min-h-0 md:shrink-0">{children}</div> : children}
          {showClose && <DialogPrimitive.Close aria-label="Close" className={cn("absolute top-3 right-3 flex size-7 items-center justify-center rounded-full text-text-tertiary outline-none", "hover:bg-fill-hover-subtle hover:text-text-primary focus-visible:ring-1 focus-visible:ring-accent")}>
              <X className="size-4" />
            </DialogPrimitive.Close>}
        </OrderedOverlayProvider>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>;
}
export function DialogTitle(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(headingVariants({
      size: "md",
      weight: "medium"
    }), "min-w-0", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <DialogPrimitive.Title className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function DialogDescription(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(textVariants({
      tone: "tertiary"
    }), className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <DialogPrimitive.Description className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

// `leading` sits before the title (an avatar or file icon); `actions` sit at
// the end of the title row, clear of the close button. `truncate` keeps long
// titles (file names) on one line with the full text as a tooltip, and
// `movable` marks the header as the drag handle of a floating dialog.
export function DialogHeader(t0) {
  const $ = _c(31);
  let actions;
  let description;
  let leading;
  let props;
  let t1;
  let t2;
  let title;
  if ($[0] !== t0) {
    ({
      title,
      description,
      leading,
      actions,
      truncate: t1,
      movable: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = actions;
    $[2] = description;
    $[3] = leading;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
    $[7] = title;
  } else {
    actions = $[1];
    description = $[2];
    leading = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
    title = $[7];
  }
  const truncate = t1 === undefined ? false : t1;
  const movable = t2 === undefined ? false : t2;
  const t3 = movable && "cursor-move touch-none select-none";
  let t4;
  if ($[8] !== t3) {
    t4 = cn("flex shrink-0 items-start gap-3 pt-4 pr-12 pb-1 pl-5", t3);
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== leading) {
    t5 = leading && <div className="flex shrink-0 items-center self-center">{leading}</div>;
    $[10] = leading;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = truncate && "truncate";
  let t7;
  if ($[12] !== t6) {
    t7 = cn(t6);
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = truncate && typeof title === "string" ? title : undefined;
  let t9;
  if ($[14] !== t7 || $[15] !== t8 || $[16] !== title) {
    t9 = <DialogTitle className={t7} title={t8}>{title}</DialogTitle>;
    $[14] = t7;
    $[15] = t8;
    $[16] = title;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== description) {
    t10 = description && <DialogDescription>{description}</DialogDescription>;
    $[18] = description;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9) {
    t11 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t9}{t10}</div>;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== actions) {
    t12 = actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>;
    $[23] = actions;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== props || $[26] !== t11 || $[27] !== t12 || $[28] !== t4 || $[29] !== t5) {
    t13 = <div className={t4} {...props}>{t5}{t11}{t12}</div>;
    $[25] = props;
    $[26] = t11;
    $[27] = t12;
    $[28] = t4;
    $[29] = t5;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  return t13;
}
const bodyPadding = {
  default: "px-5 pt-1 pb-5",
  none: ""
} as const;
export function DialogBody(t0) {
  const $ = _c(10);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      padding: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const padding = t1 === undefined ? "default" : t1;
  const t2 = bodyPadding[padding];
  let t3;
  if ($[4] !== className || $[5] !== t2) {
    t3 = cn("flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto", t2, className);
    $[4] = className;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props || $[8] !== t3) {
    t4 = <div className={t3} {...props} />;
    $[7] = props;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export function DialogFooter(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex shrink-0 items-center justify-end gap-2 border-t border-border-subtle px-4 py-3", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}